import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { sendMessage } from "@/utils/api";
import MessageList from "@/components/chat/MessageList";
import ChatInput from "@/components/chat/ChatInput";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Maximize, Minimize } from "lucide-react";

type Persona = "travel" | "health" | "meals" | "errands" | "home" | "work";

interface ChatMessage {
  id: string;
  content: string;
  sender: "user" | "assistant";
  timestamp: Date;
  files?: Array<{ name: string; url: string; type: string }>;
}

interface ChatContainerProps {
  selectedPersona: Persona | null;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const ChatContainer = ({ selectedPersona, onFullscreenChange }: ChatContainerProps) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Notify parent when fullscreen changes
  useEffect(() => {
    onFullscreenChange?.(isFullscreen);
  }, [isFullscreen, onFullscreenChange]);

  // Generate storage key based on selected persona
  const getStorageKey = (persona: Persona | null) => {
    return persona ? `sharedmatter_chat_${persona}` : "sharedmatter_chat_general";
  };

  // Load messages from sessionStorage on component mount or when persona changes
  useEffect(() => {
    const storageKey = getStorageKey(selectedPersona);
    const savedMessages = sessionStorage.getItem(storageKey);
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages).map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(parsedMessages);
        console.log(`ChatContainer: Loaded messages for ${selectedPersona || 'general'} from session:`, parsedMessages);
      } catch (error) {
        console.error("ChatContainer: Error loading messages from session:", error);
        setMessages([]);
      }
    } else {
      // No saved messages - check if we should show welcome message
      const welcomeShown = sessionStorage.getItem('welcomeMessageShown');
      const hasSeenFullWelcome = localStorage.getItem('hasSeenFullWelcome');
      const isFirstTime = localStorage.getItem('isFirstTimeLogin') === 'true';
      
      if (!welcomeShown && currentUser && selectedPersona === null) {
        let welcomeMessage: ChatMessage;
        
        if (!hasSeenFullWelcome || isFirstTime) {
          // Show full welcome message for first-time users
          welcomeMessage = {
            id: 'welcome-message',
            content: JSON.stringify({
              type: "message",
              variant: "info",
              title: "👋 Welcome to Shared Matter!",
              body: "I'm your AI assistant, here to help you stay organized, remember the important stuff, and take action across your apps. \n\nTo get started, try asking me:\n• *\"What's on my calendar today?\"*\n• *\"Remind me to call Sarah tomorrow at 10am\"*\n• *\"Summarize my recent Slack conversations\"*\n\nYou can speak or type — I'll keep up either way. Let's make your day smoother, together. 😊"
            }),
            sender: "assistant",
            timestamp: new Date(),
          };
          localStorage.setItem('hasSeenFullWelcome', 'true');
        } else {
          // Show shorter welcome message for returning users
          welcomeMessage = {
            id: 'welcome-message',
            content: JSON.stringify({
              type: "message",
              variant: "info",
              title: "👋 Welcome back!",
              body: "Ready to tackle your day? I'm here to help with your calendar, reminders, and staying organized. What can I do for you today?"
            }),
            sender: "assistant",
            timestamp: new Date(),
          };
        }
        
        setMessages([welcomeMessage]);
        sessionStorage.setItem('welcomeMessageShown', 'true');
        if (isFirstTime) {
          localStorage.removeItem('isFirstTimeLogin');
        }
      } else {
        setMessages([]);
      }
    }
    setHasInitialLoad(true);
  }, [selectedPersona, currentUser]);

  // Save messages to sessionStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0 && hasInitialLoad) {
      const storageKey = getStorageKey(selectedPersona);
      sessionStorage.setItem(storageKey, JSON.stringify(messages));
      console.log(`ChatContainer: Saved messages for ${selectedPersona || 'general'} to session:`, messages);
    }
  }, [messages, selectedPersona, hasInitialLoad]);

  const scrollToBottom = (force = false) => {
    // Only auto-scroll after initial load and when not on mobile, or when forced (new messages)
    if (hasInitialLoad && (force || !isMobile)) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  // Only scroll to bottom for new messages, not on initial load
  useEffect(() => {
    if (hasInitialLoad && messages.length > 0) {
      scrollToBottom(true);
    }
  }, [messages, hasInitialLoad]);

  // Prevent viewport jumping on mobile when keyboard appears
  useEffect(() => {
    if (isMobile) {
      const preventViewportJump = (e: Event) => {
        e.preventDefault();
      };

      // Prevent scroll restoration
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      // Add event listeners to prevent unwanted scrolling
      window.addEventListener('scroll', preventViewportJump, { passive: false });
      
      return () => {
        window.removeEventListener('scroll', preventViewportJump);
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'auto';
        }
      };
    }
  }, [isMobile]);

  // Convert ChatMessage to Message format for MessageList
  const convertedMessages = messages.map(msg => ({
    id: msg.id,
    text: msg.content,
    sender: msg.sender,
    timestamp: msg.timestamp,
    file: msg.files?.[0] ? { name: msg.files[0].name } : undefined
  }));

  // Add processing message when loading
  const messagesWithProcessing = isLoading 
    ? [...convertedMessages, {
        id: 'processing',
        text: 'Please wait while we process your request',
        sender: 'assistant' as const,
        timestamp: new Date(),
        isProcessing: true
      }]
    : convertedMessages;

  const handleSendMessage = async (inputValue: string, uploadedFiles: Array<{ name: string; url: string; type: string; file?: File; fileType?: string; fileName?: string }>) => {
    if (!inputValue.trim() || isLoading) return;

    console.log("ChatContainer: handleSendMessage called with input:", inputValue);
    console.log("ChatContainer: Files uploaded:", uploadedFiles);
    console.log("ChatContainer: Number of files:", uploadedFiles.length);
    console.log("ChatContainer: Selected persona:", selectedPersona);

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: inputValue,
      sender: "user",
      timestamp: new Date(),
      files: uploadedFiles.length > 0 ? uploadedFiles.map(({ file, ...rest }) => rest) : undefined,
    };

    console.log("ChatContainer: Created user message:", userMessage);

    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      console.log("ChatContainer: Adding user message, new messages array:", newMessages);
      return newMessages;
    });
    
    setIsLoading(true);

    try {
      console.log("ChatContainer: Calling sendMessage...");
      
      // Get sessionId from localStorage (generated in AuthContext)
      const sessionId = localStorage.getItem('sessionId') || undefined;
      const userId = currentUser?.id;
      
      console.log("ChatContainer: Session ID:", sessionId);
      console.log("ChatContainer: User ID:", userId);
      
      // Convert files to the format expected by the API with File objects and fileType
      const apiFiles = uploadedFiles.map(file => ({
        name: file.name,
        url: file.url,
        type: file.type,
        file: file.file, // Include the File object for FormData
        fileType: file.fileType // Include the fileType for web URLs
      }));
      
      console.log("ChatContainer: Sending to API with files:", apiFiles);
      console.log("ChatContainer: messageType will be:", uploadedFiles.length > 0 ? "file" : "chat");
      console.log("ChatContainer: File details for API:");
      apiFiles.forEach((file, index) => {
        console.log(`  File ${index}:`, {
          name: file.name,
          type: file.type,
          url: file.url,
          hasFileObject: !!file.file,
          fileSize: file.file?.size
        });
      });
      
      const response = await sendMessage(
        inputValue, 
        selectedPersona || "general", 
        apiFiles,
        sessionId,
        userId
      );
      
      console.log("ChatContainer: Received response:", response);
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: response.message,
        sender: "assistant",
        timestamp: new Date(),
      };

      console.log("ChatContainer: Creating assistant message:", assistantMessage);
      
      setMessages(prev => {
        console.log("ChatContainer: Previous messages before adding assistant:", prev);
        const newMessages = [...prev, assistantMessage];
        console.log("ChatContainer: New messages array after adding assistant:", newMessages);
        return newMessages;
      });
      
      console.log("ChatContainer: Assistant message should now be added to state");
    } catch (error) {
      console.error("ChatContainer: Error sending message:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send message. Please try again.",
      });
    } finally {
      setIsLoading(false);
      console.log("ChatContainer: Setting isLoading to false");
    }
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-32 sm:h-48 lg:h-64">
        <p className="text-gray-500 text-xs sm:text-sm lg:text-base px-4 text-center">Please sign in to use the chat.</p>
      </div>
    );
  }

  return (
    <div 
      className={`chat-container flex flex-col overflow-hidden ${
        isFullscreen 
          ? 'fixed inset-4 z-50 rounded-2xl' 
          : isMobile 
            ? 'h-[70vh] max-h-[600px] min-h-[400px]' 
            : 'h-[400px] sm:h-[500px]'
      }`}
      style={isMobile ? {
        position: 'relative',
        transform: 'translateZ(0)', // Force hardware acceleration
      } : {}}
    >

      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 sm:p-6 scroll-smooth"
        style={{ 
          scrollBehavior: 'smooth',
          // Prevent the container from bouncing on iOS
          WebkitOverflowScrolling: 'touch',
          // Prevent unwanted scrolling on mobile
          ...(isMobile && {
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
          })
        }}
      >
        <MessageList messages={messagesWithProcessing} isLoading={false} />
        <div ref={messagesEndRef} />
      </div>
      
      <ChatInput 
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        selectedPersona={selectedPersona}
      />
    </div>
  );
};

export default ChatContainer;
